import { Badge, Box, Card, Typography } from '@veraclins-dev/ui';

import { homeContent } from './content/home';
import { AppLink } from './link';

const brandButtonClassName =
  'inline-flex items-center justify-center rounded-lg border border-primary bg-primary px-5.5 py-3 font-medium text-primary-foreground transition hover:-translate-y-px';

const outlineButtonClassName =
  'inline-flex items-center justify-center rounded-lg border border-primary/30 bg-transparent px-5.5 py-3 font-medium text-foreground transition hover:-translate-y-px hover:border-primary/60';

const cardLinkClassName =
  'block rounded-3xl border border-primary/12 bg-card p-5 sm:p-6 no-underline shadow-[0_12px_32px_-20px_rgba(40,20,10,.5)] transition hover:-translate-y-0.5';

export function App() {
  const { hero, stats, featured, about } = homeContent;

  return (
    <Box display="flex" flexDirection="column">
      {/* hero */}
      <Box
        component="section"
        display="grid"
        gap={10}
        pt={12}
        pb={12}
        items="center"
        // oxlint-disable-next-line veraclins-ui/prefer-style-props -- 1.55fr/1fr grid template has no scale-token/prop equivalent
        className="lg:grid-cols-[1.55fr_1fr] lg:gap-14 lg:pt-16"
        data-testid="hero-section"
      >
        <Box display="flex" flexDirection="column" items="start">
          <Badge
            variant="soft"
            tone="success"
            pill
            // oxlint-disable-next-line veraclins-ui/prefer-style-props -- mb-6 gap and inline-flex badge layout tuned to the reference pill
            className="mb-6 inline-flex items-center gap-2 py-1.5 text-sm/normal font-semibold"
          >
            <Box
              component="span"
              aria-hidden="true"
              rounded="full"
              size={2}
              className="bg-success"
            />
            {hero.availability}
          </Badge>
          <Typography
            variant="h1"
            // oxlint-disable-next-line veraclins-ui/prefer-style-props -- clamp() font-size and tight tracking have no scale-token/prop equivalent
            className="mb-5 max-w-[15ch] font-sans text-[clamp(2.5rem,5vw,3.75rem)] font-extrabold leading-none tracking-tight text-balance"
          >
            {hero.title}
          </Typography>
          <Typography
            variant="body1"
            // oxlint-disable-next-line veraclins-ui/prefer-style-props -- 52ch max-width and 19px size have no scale-token/prop equivalent
            className="mb-8 max-w-[52ch] text-xl leading-relaxed text-foreground/75"
          >
            {hero.lede}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={3.5} className="text-base">
            <AppLink
              to={hero.primaryCta.href}
              tone="neutral"
              className={brandButtonClassName}
            >
              {hero.primaryCta.label}
            </AppLink>
            <AppLink
              to={hero.secondaryCta.href}
              tone="neutral"
              className={outlineButtonClassName}
            >
              {hero.secondaryCta.label}
            </AppLink>
          </Box>
        </Box>
        <Box
          display="flex"
          justify="center"
          // oxlint-disable-next-line veraclins-ui/prefer-style-props -- reverse the hero on mobile so the monogram sits above the copy; reset to DOM order (monogram right) once the two-column grid kicks in
          className="order-first lg:order-none"
        >
          <Box
            aria-hidden="true"
            display="flex"
            items="center"
            justify="center"
            rounded="full"
            // oxlint-disable-next-line veraclins-ui/prefer-style-props -- 240px (size-60) avatar exceeds the size style-prop scale
            className="size-60 bg-[linear-gradient(150deg,var(--primary),color-mix(in_oklch,var(--primary)_55%,#a22))] text-8xl/normal font-extrabold text-primary-foreground shadow-[0_22px_56px_-16px_color-mix(in_oklch,var(--primary)_70%,transparent)]"
          >
            {hero.monogram}
          </Box>
        </Box>
      </Box>

      {/* stats */}
      <Box
        component="section"
        display="grid"
        gridCols={{ xs: 2, lg: 4 }}
        gap={5}
        py={2}
        aria-label="At a glance"
      >
        {stats.map((stat) => (
          <Card
            key={stat.label}
            px={6}
            py={5}
            // oxlint-disable-next-line veraclins-ui/prefer-style-props -- 18px radius and primary-tinted border have no scale-token/prop equivalent
            className="rounded-2xl border-primary/12"
          >
            <Typography
              variant="inherit"
              component="span"
              display="block"
              className="text-3xl font-extrabold leading-none tracking-tight"
            >
              {stat.value}
            </Typography>
            <Typography
              variant="inherit"
              component="span"
              display="block"
              mt={1}
              className="text-sm/normal text-foreground/60"
            >
              {stat.label}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* featured */}
      <Box
        component="section"
        pt={14}
        pb={5}
        aria-labelledby="featured-heading"
      >
        <Box
          mb={6}
          display="flex"
          flexWrap="wrap"
          items="end"
          justify="between"
          gap={4}
        >
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography
              variant="h2"
              id="featured-heading"
              className="font-sans text-3xl font-extrabold leading-tight tracking-tight"
            >
              {featured.title}
            </Typography>
            <Typography
              variant="body1"
              className="text-base text-foreground/60"
            >
              {featured.subtitle}
            </Typography>
          </Box>
          <AppLink
            to={featured.allHref}
            tone="brand"
            className="text-sm/normal font-semibold text-primary"
          >
            {featured.allLabel} →
          </AppLink>
        </Box>

        <Box display="grid" gridCols={{ xs: 1, md: 3 }} gap={5}>
          {featured.items.map((item) => (
            <AppLink
              key={item.title}
              to={item.href}
              tone="neutral"
              className={cardLinkClassName}
            >
              <Badge
                variant="soft"
                tone="brand"
                pill
                // oxlint-disable-next-line veraclins-ui/prefer-style-props -- uppercase micro-label spacing matches the reference tag
                className="mb-4 text-xs/normal font-bold uppercase tracking-wider"
              >
                {item.tag}
              </Badge>
              <Typography
                variant="inherit"
                component="span"
                display="block"
                marginBottom={1.5}
                className="text-xl/normal font-bold"
              >
                {item.title}
              </Typography>
              <Typography
                variant="body1"
                marginBottom={3.5}
                className="text-sm leading-relaxed text-foreground/70"
              >
                {item.description}
              </Typography>
              <Typography
                component="span"
                variant="inherit"
                className="text-sm/normal font-semibold text-primary"
              >
                {item.cta} →
              </Typography>
            </AppLink>
          ))}

          <AppLink
            to={featured.products.href}
            tone="neutral"
            className="block rounded-3xl p-5 sm:p-6 no-underline text-primary-foreground shadow-[0_12px_32px_-20px_rgba(40,20,10,.5)] transition hover:-translate-y-0.5 bg-[linear-gradient(150deg,var(--primary),color-mix(in_oklch,var(--primary)_58%,#a22))]"
          >
            <Box
              component="span"
              display="inline-block"
              mb={4}
              paddingY={1}
              rounded="full"
              paddingX={2.5}
              className="bg-primary-foreground/20 text-xs/normal font-bold uppercase tracking-wider text-primary-foreground"
            >
              {featured.products.tag}
            </Box>
            <Typography
              variant="inherit"
              component="span"
              display="block"
              marginBottom={1.5}
              className="text-xl/normal font-bold"
            >
              {featured.products.title}
            </Typography>
            <Typography
              variant="body1"
              marginBottom={3.5}
              className="text-sm leading-relaxed text-primary-foreground/90"
            >
              {featured.products.description}
            </Typography>
            <Typography
              component="span"
              variant="inherit"
              className="text-sm/normal font-bold text-primary-foreground"
            >
              {featured.products.cta} →
            </Typography>
          </AppLink>
        </Box>
      </Box>

      {/* about teaser */}
      <Box component="section" pt={14} pb={14}>
        <Card
          // oxlint-disable-next-line veraclins-ui/prefer-style-props -- 26px radius, primary-tinted border and 1fr/1.5fr grid have no scale-token/prop equivalent
          className="grid gap-8 rounded-3xl border-primary/12 p-6 md:grid-cols-[1fr_1.5fr] md:items-center md:gap-12 md:p-12"
        >
          <Box display="flex" flexDirection="column">
            <Typography
              component="span"
              variant="inherit"
              display="block"
              marginBottom={3.5}
              className="text-xs font-bold uppercase tracking-widest text-primary"
            >
              {about.eyebrow}
            </Typography>
            <Typography
              variant="h3"
              className="font-sans text-2xl font-extrabold leading-tight tracking-tight"
            >
              {about.heading}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" items="start" gap={4}>
            <Typography
              variant="body1"
              className="text-base leading-relaxed text-foreground/80"
            >
              {about.body}
            </Typography>
            <AppLink
              to={about.href}
              tone="brand"
              className="text-sm/normal font-semibold text-primary"
            >
              {about.cta} →
            </AppLink>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}

// oxlint-disable-next-line veraclins-ui/named-exports-only -- route module: registered as index('./app.tsx') in routes.tsx, requires a default export
export default App;
